import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {Flash, Spinner} from '@primer/react'
import {clsx} from 'clsx'

import React from 'react'

import {RenderState, useFileRenderer} from './use-file-renderer'

import styles from './FileRendererBlob.module.css'

const MAX_BLOB_SIZE = 200_000_000 // 200MB
const MAX_NOTEBOOK_SIZE = 30_000_000 // 30MB

export interface FileRendererBlobData {
  identityUuid: string
  size: number
  type: string
  url: string
}

type FileRendererBlobProps = FileRendererBlobData & {
  iframeClassName?: string
  /** Defaults to height of 500px unless overridden by this class */
  viewportClassName?: string
}

export default function FileRendererBlob({
  identityUuid,
  size,
  type,
  url,
  iframeClassName,
  viewportClassName,
}: FileRendererBlobProps) {
  const {isBrowser} = useRuntimeEnvironment()
  const tempOrigin = isBrowser ? (ssrSafeLocation?.origin ?? 'https://www.github.com') : 'https://www.github.com'
  const origin = new URL(url, tempOrigin).origin
  const {renderState, errorMsg, iFrameRef, containerRef} = useFileRenderer(origin, identityUuid)

  const isNotebook = type === 'ipynb'
  if ((isNotebook && size > MAX_NOTEBOOK_SIZE) || size > MAX_BLOB_SIZE) {
    return <Flash variant="danger">Sorry, this is too big to display.</Flash>
  }

  return (
    <div className={styles.FileRendererWrapper}>
      <div
        data-hpc
        data-host={origin}
        data-type={type}
        ref={containerRef}
        className={clsx(
          styles.FileRendererViewport,
          {
            [styles.FileRendererViewportNonError]: renderState !== RenderState.ERROR,
            [styles.FileRendererViewportReady]: renderState === RenderState.READY,
          },
          viewportClassName,
        )}
      >
        {renderState === RenderState.ERROR ? (
          <FileRendererErrorMessage error={errorMsg} />
        ) : renderState !== RenderState.READY ? (
          <Spinner size="large" className={styles.loadingIndicator} />
        ) : null}

        {renderState !== RenderState.ERROR && (
          <iframe
            ref={iFrameRef}
            src={`${url}#${identityUuid}`}
            // eslint-disable-next-line @eslint-react/dom/no-unsafe-iframe-sandbox
            sandbox="allow-scripts allow-same-origin allow-top-navigation"
            name={identityUuid}
            title="File display"
            className={clsx(
              styles.fileContentFrame,
              {
                [styles.fileContentFrameNotReady]: renderState !== RenderState.READY,
              },
              iframeClassName,
            )}
          >
            Viewer requires iframe.
          </iframe>
        )}
      </div>
    </div>
  )
}

function FileRendererErrorMessage({error}: {error: string | null}) {
  if (!error) {
    return <Flash variant="danger">Unable to render code block</Flash>
  }

  const msgLines = error.split('\n')
  return (
    <Flash variant="danger">
      <p className={styles.errorMessageHeading}>Error rendering embedded code</p>
      <p>
        {msgLines.map((line, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <React.Fragment key={`error-line-${index}`}>
            {line}
            <br />
          </React.Fragment>
        ))}
      </p>
    </Flash>
  )
}
